import type { ApiEnvelope } from "@/lib/qa-types";

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api";
const csrfStorageKey = "qascope.csrf-token";

export interface AdminUser {
    id: string;
    username: string;
    display_name: string;
    roles: string[];
}

export interface LoginSession {
    user: AdminUser;
    csrf_token: string;
    expires_at: string;
}

interface ApiErrorData {
    error_code?: string;
}

export class ApiRequestError extends Error {
    constructor(
        public readonly status: number,
        public readonly errorCode: string,
        message: string,
    ) {
        super(message);
        this.name = "ApiRequestError";
    }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
        response = await fetch(`${apiBaseUrl}${path}`, {
            ...init,
            credentials: "include",
            signal: init.signal ?? AbortSignal.timeout(5000),
            headers: { Accept: "application/json", "Content-Type": "application/json", ...init.headers },
        });
    } catch {
        throw new ApiRequestError(0, "NETWORK_ERROR", "无法连接后端服务，请确认 API 已启动");
    }

    let envelope: ApiEnvelope<T> | (ApiEnvelope<ApiErrorData> & { data: ApiErrorData });
    try {
        envelope = (await response.json()) as typeof envelope;
    } catch {
        throw new ApiRequestError(response.status, "INVALID_RESPONSE", "后端返回了无法识别的响应");
    }
    if (!response.ok) {
        const errorData = envelope.data as ApiErrorData;
        throw new ApiRequestError(response.status, errorData?.error_code ?? "REQUEST_FAILED", envelope.msg || "请求失败");
    }
    return envelope.data as T;
}

export async function login(username: string, password: string): Promise<LoginSession> {
    const session = await apiRequest<LoginSession>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username: username.trim(), password }),
    });
    storeCsrfToken(session.csrf_token);
    return session;
}

export async function loadCurrentUser(): Promise<AdminUser | null> {
    try {
        return await apiRequest<AdminUser>("/auth/me", { method: "GET" });
    } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
    }
}

export async function logout(csrfToken: string | null): Promise<void> {
    await apiRequest<{ success: boolean }>("/auth/logout", {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : undefined,
    });
    clearStoredCsrfToken();
}

export function loadStoredCsrfToken(): string | null {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(csrfStorageKey);
}

export function storeCsrfToken(token: string): void {
    if (typeof window !== "undefined") window.sessionStorage.setItem(csrfStorageKey, token);
}

export function clearStoredCsrfToken(): void {
    if (typeof window !== "undefined") window.sessionStorage.removeItem(csrfStorageKey);
}
